import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { CollectionCard } from '../../../models/collections/collection-card.model';
import { CollectionCardRankType } from '../../../enums/collections/collection-card-rank-type.enum';
import { ApiCollectionsService } from '../../../services/collections/api/api-collections.service';
import { AlertsService } from '../../../services/alerts.service';
import { ModalsService } from '../../../services/modals.service';
import { LanguagesService } from '../../../services/languages/languages.service';
import { getMissingTranslationsCount } from '../collection-translation-form.model';
import { CollectionsImagesHelper } from '../../../utils/helpers/collections-images.helper';
import { CollectionCardAddModalComponent } from './modals/collection-card-add-modal/collection-card-add-modal.component';
import { CollectionCardEditModalComponent } from './modals/collection-card-edit-modal/collection-card-edit-modal.component';

@Component({
  templateUrl: 'collection-cards.component.html',
  standalone: true,
  imports: [CommonModule, RouterModule, TooltipModule, CollectionCardAddModalComponent, CollectionCardEditModalComponent]
})
export class CollectionCardsComponent implements OnInit {

  @ViewChild('addModal') addModal!: CollectionCardAddModalComponent;
  @ViewChild('editModal') editModal!: CollectionCardEditModalComponent;
  @ViewChild('imageInput') imageInput!: ElementRef<HTMLInputElement>;

  protected readonly CollectionCardRankType = CollectionCardRankType;

  collectionId: number = 0;
  albumId: number = 0;
  cards: CollectionCard[] = [];
  isLoading: boolean = false;
  uploadingImageId: number = 0;
  totalResults: number = 0;
  resultsPerPage: number = 25;
  pageNumber: number = 1;

  private imageUploadTarget: CollectionCard | null = null;

  get totalPages(): number {
    return Math.ceil(this.totalResults / this.resultsPerPage);
  }

  getPageNumbers(): (number | null)[] {
    const total = this.totalPages;
    const current = this.pageNumber;
    const visibleSet = new Set<number>();
    for (let i = 1; i <= Math.min(2, total); i++) visibleSet.add(i);
    for (let i = Math.max(total - 1, 1); i <= total; i++) visibleSet.add(i);
    for (let i = Math.max(1, current - 2); i <= Math.min(total, current + 2); i++) visibleSet.add(i);
    const sorted = Array.from(visibleSet).sort((a, b) => a - b);
    const result: (number | null)[] = [];
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push(null);
      result.push(sorted[i]);
    }
    return result;
  }

  constructor(
    private route: ActivatedRoute,
    private apiCollectionsService: ApiCollectionsService,
    private alertsService: AlertsService,
    private modalsService: ModalsService,
    private languagesService: LanguagesService
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      const id: string = params['id'];
      this.collectionId = Number(params['collectionId'] ?? 0);
      if (id) {
        this.albumId = Number(id);
        this.pageNumber = 1;
        this.refreshCards();
      }
    });
  }

  onClickPaginationPrevious(): void {
    this.pageNumber--;
    this.refreshCards();
  }

  onClickPaginationNext(): void {
    this.pageNumber++;
    this.refreshCards();
  }

  goToPage(page: number): void {
    this.pageNumber = page;
    this.refreshCards();
  }

  getMissingTranslationsCount(card: CollectionCard): number {
    return getMissingTranslationsCount(this.languagesService.languages, card.translations);
  }

  hasImage(card: CollectionCard): boolean {
    return CollectionsImagesHelper.hasImage(card.imageUpdatedAt);
  }

  getImageUrl(card: CollectionCard): string {
    return CollectionsImagesHelper.getImageUrl('card', card.cardId, card.imageUpdatedAt);
  }

  onClickUploadImage(card: CollectionCard): void {
    this.imageUploadTarget = card;
    this.imageInput.nativeElement.click();
  }

  onImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    const card = this.imageUploadTarget;
    input.value = '';
    this.imageUploadTarget = null;
    if (!file || !card) return;
    const fileError = CollectionsImagesHelper.getImageFileError(file);
    if (fileError) {
      this.alertsService.displayAlertError(fileError);
      return;
    }
    this.uploadingImageId = card.cardId;
    this.apiCollectionsService.uploadCollectionAlbumCardImage(card.cardId, file).then(
      () => {
        this.uploadingImageId = 0;
        this.alertsService.displayAlertSuccess('Image uploaded !');
        this.refreshCards();
      }, error => {
        this.uploadingImageId = 0;
        this.alertsService.displayAlertError('Error uploading image : ' + error);
      }
    );
  }

  onClickAdd(): void {
    this.addModal.show(() => this.refreshCards());
  }

  onClickEdit(card: CollectionCard): void {
    this.editModal.show(card, () => this.refreshCards());
  }

  onClickDelete(card: CollectionCard): void {
    this.modalsService.showConfirmModal(() => {
      this.apiCollectionsService.deleteCollectionAlbumCard(card.cardId).then(
        () => {
          this.alertsService.displayAlertSuccess('Card deleted !');
          this.refreshCards();
        }, error => {
          this.alertsService.displayAlertError('Error deleting card : ' + error);
        }
      );
    });
  }

  private refreshCards(): void {
    this.isLoading = true;
    this.apiCollectionsService.getCollectionAlbumCards(this.albumId, this.pageNumber, this.resultsPerPage).then(
      response => {
        this.isLoading = false;
        this.cards = response.results;
        this.totalResults = response.resultsTotal;
      }, error => {
        this.isLoading = false;
        this.alertsService.displayAlertError('Error loading cards : ' + error);
      }
    );
  }
}
